import { ApiKey } from "@closerplatform/closer-sdk";
import { ID } from "@closerplatform/closer-sdk/dist/protocol/protocol";
import {
  AcceptInvitationForm,
  AddWidgetForm,
  AdminSetNames,
  AdviserBatchPatchForm,
  AdviserCobrowseJwtResponse,
  AdviserPatchForm,
  AdviserToast,
  AgentCtx,
  AgentGroup,
  AgentGroupBatchPatchDto,
  AgentGroupPatchDto,
  AgentGroupsApi,
  AdviserAverageSpeedOfAnswerDto,
  AgentPreferences,
  AgentPresence,
  AgentProfile,
  AgentProfileForm,
  AgentStatusesApi,
  AuthCode,
  AutoAssign,
  BillingPlan,
  BillingsApi,
  BotPersonalDataForm,
  BotsApi,
  CalendarForm,
  CalendarInfo,
  CalendarsApi,
  CalendarSlot,
  CobrowsingApi,
  CodeAuthorizationForm,
  Configuration,
  ConversationsApi,
  ConversationStatus,
  CreateAgentGroupForm,
  CreateEventActionConfigForm,
  CreateProactiveMsgForm,
  CreateRoleForm,
  CreateTagForm,
  CreateTagGroupForm,
  CreateTagMappingForm,
  DatasetEntryDto,
  DatasetEntryForm,
  EnabledFlag,
  EventActionConfig,
  EventActionsApi,
  FilesApi,
  Follower,
  FreeOrgAdminForm,
  GuestProfile,
  GuestProfileForm,
  GuestProfileFormPatch,
  InboxEntry,
  InboxState,
  IntentDto,
  IntentForm,
  IntentWithDatasetInfo,
  Invitee,
  InviteesApi,
  InviteManyAdvisersForm,
  LoginCredentialsOrTokenForm,
  MagicLinkForm,
  MainNotificationDto,
  MainNotificationPatchForm,
  MainNotificationPostForm,
  MainNotificationsApi,
  Meeting,
  MeetingsApi,
  MessageWidgetConfig,
  MessageWidgetForm,
  MessageWidgetsApi,
  NewModel,
  NLUApi,
  Nonce,
  OIDCApi,
  OpenIDConnectConfig,
  OrganizationLimits,
  OrgBillingData,
  OrgConfig,
  OrgInvitationForm,
  OrgReasons,
  OrgsApi,
  PasswordChangeForm,
  PasswordChangeWithTokenForm,
  PasswordResetForm,
  PlanDetails,
  PortalUrlResponse,
  PreferencesForm,
  ProactiveMsgApi,
  ProactiveMsgDto,
  ProfanitiesApi,
  ProfanitiesDto,
  ProfanityForm,
  ResendInvitationForm,
  Role,
  RolesApi,
  RoomsApi,
  SessionApi,
  SessionIdResponse,
  StripeOrgAdminForm,
  SuggestionTestResult,
  TagDto,
  TagGroupDto,
  TagGroupsApi,
  TagGroupTransfersApi,
  TagGroupTransfersParametersApi,
  TagMappingDto,
  TagMappingsApi,
  TagsApi,
  TestSuggestionForm,
  TopicDTO,
  TopicsApi,
  TrainingInfo,
  TrainModelForm,
  TransferParametersDto,
  TransferRuleDto,
  TransferRulePatchForm,
  UnavailabilityReason,
  UnavailabilityReasonCreateForm,
  UnavailabilityReasonUpdateForm,
  UnreadApi,
  UnreadState,
  UpdateEventActionConfigForm,
  UpdateProactiveMsgForm,
  UpdateRoleForm,
  UpdateTagForm,
  UpdateTagGroupForm,
  UpdateTagMappingForm,
  UpgradeForm,
  UsersApi,
  VatRequestForm,
  VatResponse,
  WebhooksApi,
  WebhooksDTO,
  WidgetFormSubmission,
  WidgetFormSubmissionWithWidget,
  WidgetsFormsSubmissionsApi
} from "@closerplatform/spinner-openapi";
import { config } from "@fidget/common/config/config";
import { getWheelhouseDownloadUrl } from "@fidget/common/utils/urlMappingHelpers";
import { IAttachment } from "@fidget/common/models/chat/chatItems";
import { IFileUploadInfo } from "@fidget/common/models/fileUpload";
import { PubSub } from "@fidget/common/services/pubSub";
import { logger } from "@fidget/common/utils";
import { Timed } from "@fidget/common/utils/timed";
import { mapUploadResponse } from "@fidget/common/utils/fileUploadResponses";
import { GetConversationsParams, SpinnerService } from "./spinnerService";

@Timed
export class DefaultSpinnerService extends PubSub implements SpinnerService {
  private configuration = new Configuration({ basePath: config.services.spinner.baseUrl });
  private orgId?: string;

  private agentGroupsApi = new AgentGroupsApi(this.configuration);
  private conversationsApi = new ConversationsApi(this.configuration);
  private roomsApi = new RoomsApi(this.configuration);
  private usersApi = new UsersApi(this.configuration);
  private orgsApi = new OrgsApi(this.configuration);
  private tagsApi = new TagsApi(this.configuration);
  private tagGroupsApi = new TagGroupsApi(this.configuration);
  private tagMappingsApi = new TagMappingsApi(this.configuration);
  private nluApi = new NLUApi(this.configuration);
  private rolesApi = new RolesApi(this.configuration);
  private topicsApi = new TopicsApi(this.configuration);
  private billingsApi = new BillingsApi(this.configuration);
  private sessionApi = new SessionApi(this.configuration);
  private unreadApi = new UnreadApi(this.configuration);
  private agentStatusesApi = new AgentStatusesApi(this.configuration);
  private profanitiesApi = new ProfanitiesApi(this.configuration);
  private filesApi = new FilesApi(this.configuration);
  private meetingsApi = new MeetingsApi(this.configuration);
  private inviteesApi = new InviteesApi(this.configuration);
  private proactiveMsgApi = new ProactiveMsgApi(this.configuration);
  private eventActionsApi = new EventActionsApi(this.configuration);
  private oidcApi = new OIDCApi(this.configuration);
  private webhooksAPi = new WebhooksApi(this.configuration);
  private cobrowsingApi = new CobrowsingApi(this.configuration);
  private botsApi = new BotsApi(this.configuration);
  private messageWidgetsApi = new MessageWidgetsApi(this.configuration);
  private widgetsFormsSubmissionsApi = new WidgetsFormsSubmissionsApi(this.configuration);
  private calendarsApi = new CalendarsApi(this.configuration);
  private mainNotificationsApi = new MainNotificationsApi(this.configuration);
  private tagGroupTransfersApi = new TagGroupTransfersApi(this.configuration);
  private tagGroupTransfersParametersApi = new TagGroupTransfersParametersApi(this.configuration);

  private get apiKey(): string | undefined {
    return this.configuration.apiKey && typeof this.configuration.apiKey === "string"
      ? this.configuration.apiKey
      : undefined;
  }

  private doFetch(input: RequestInfo, payload?: Partial<RequestInit>) {
    const headers = this.apiKey
      ? { ...payload?.headers, "X-Api-Key": this.apiKey }
      : payload?.headers;
    return fetch(input, { ...payload, headers });
  }

  private doFileFetch(token: string, file: File, fileName?: string) {
    const body = new FormData();
    body.append("file", file, fileName);
    const fileUrl = getWheelhouseDownloadUrl(token);
    return this.doFetch(fileUrl, { method: "POST", body });
  }

  connect(orgId: string, apiKey: ApiKey) {
    this.orgId = orgId;
    this.configuration = new Configuration({ basePath: this.configuration.basePath, apiKey });

    this.agentGroupsApi = new AgentGroupsApi(this.configuration);
    this.conversationsApi = new ConversationsApi(this.configuration);
    this.roomsApi = new RoomsApi(this.configuration);
    this.usersApi = new UsersApi(this.configuration);
    this.orgsApi = new OrgsApi(this.configuration);
    this.tagsApi = new TagsApi(this.configuration);
    this.nluApi = new NLUApi(this.configuration);
    this.rolesApi = new RolesApi(this.configuration);
    this.topicsApi = new TopicsApi(this.configuration);
    this.billingsApi = new BillingsApi(this.configuration);
    this.sessionApi = new SessionApi(this.configuration);
    this.unreadApi = new UnreadApi(this.configuration);
    this.agentStatusesApi = new AgentStatusesApi(this.configuration);
    this.profanitiesApi = new ProfanitiesApi(this.configuration);
    this.filesApi = new FilesApi(this.configuration);
    this.meetingsApi = new MeetingsApi(this.configuration);
    this.inviteesApi = new InviteesApi(this.configuration);
    this.proactiveMsgApi = new ProactiveMsgApi(this.configuration);
    this.eventActionsApi = new EventActionsApi(this.configuration);
    this.oidcApi = new OIDCApi(this.configuration);
    this.webhooksAPi = new WebhooksApi(this.configuration);
    this.cobrowsingApi = new CobrowsingApi(this.configuration);
    this.botsApi = new BotsApi(this.configuration);
    this.messageWidgetsApi = new MessageWidgetsApi(this.configuration);
    this.widgetsFormsSubmissionsApi = new WidgetsFormsSubmissionsApi(this.configuration);
    this.tagGroupsApi = new TagGroupsApi(this.configuration);
    this.tagMappingsApi = new TagMappingsApi(this.configuration);
    this.calendarsApi = new CalendarsApi(this.configuration);
    this.mainNotificationsApi = new MainNotificationsApi(this.configuration);
    this.tagGroupTransfersApi = new TagGroupTransfersApi(this.configuration);
    this.tagGroupTransfersParametersApi = new TagGroupTransfersParametersApi(this.configuration);
  }

  login(loginCredentialsOrTokenForm: LoginCredentialsOrTokenForm): Promise<AgentCtx> {
    return this.sessionApi.login(loginCredentialsOrTokenForm, { credentials: "include" });
  }

  async logout(): Promise<void> {
    await this.sessionApi.logout({ credentials: "include" });
  }

  getSession(): Promise<AgentCtx> {
    return this.sessionApi.getSession({ credentials: "include" });
  }

  async resetPassword(passwordResetForm: PasswordResetForm): Promise<void> {
    await this.sessionApi.resetPassword(passwordResetForm, { credentials: "include" });
  }

  async changePasswordWithToken(
    passwordChangeWithTokenForm: PasswordChangeWithTokenForm
  ): Promise<void> {
    await this.sessionApi.changePasswordWithToken(passwordChangeWithTokenForm, {
      credentials: "include"
    });
  }

  async magiclink(magicLinkForm: MagicLinkForm): Promise<void> {
    await this.sessionApi.magiclink(magicLinkForm, { credentials: "include" });
  }

  async changePassword(passwordChangeForm: PasswordChangeForm): Promise<void> {
    await this.usersApi.changePassword(passwordChangeForm);
  }

  acceptInvitation(token: string, acceptInvitationForm: AcceptInvitationForm): Promise<AgentCtx> {
    return this.inviteesApi.acceptInvitation(acceptInvitationForm, token);
  }

  getUnreadCounters(): Promise<UnreadState> {
    return this.unreadApi.getUnreadCounters();
  }

  createStripeOrgAdmin(
    token: string,
    stripeOrgAdminForm: StripeOrgAdminForm
  ): Promise<SessionIdResponse> {
    return this.orgsApi.createStripeOrgAdmin(stripeOrgAdminForm, token);
  }

  createFreeOrgAdmin(token: string, freeOrgAdminForm: FreeOrgAdminForm): Promise<AgentCtx> {
    return this.orgsApi.createFreeOrgAdmin(freeOrgAdminForm, token);
  }

  upgradeOrg(upgradeForm: UpgradeForm): Promise<SessionIdResponse> {
    return this.orgsApi.upgradeOrg(upgradeForm);
  }

  async setAgentProfile(agentProfileForm: AgentProfileForm): Promise<void> {
    await this.usersApi.setAgentProfile(agentProfileForm);
  }

  getOrganizationConfig(orgId: string = this.orgId || ""): Promise<OrgConfig> {
    return this.orgsApi.getOrganizationConfig(orgId);
  }

  getLimits(orgId: string): Promise<OrganizationLimits> {
    return this.orgsApi.getLimits(orgId);
  }

  getOwnPlan(): Promise<BillingPlan> {
    return this.billingsApi.getOwnPlan();
  }

  async deleteAgentAvatar(): Promise<void> {
    await this.usersApi.deleteAgentAvatar();
  }

  getTokenValidity(token: string): Promise<AdminSetNames> {
    return this.inviteesApi.getTokenValidity(token);
  }

  getOrgTokenValidity(token: string): Promise<PlanDetails> {
    return this.orgsApi.getOrgTokenValidity(token);
  }

  async inviteOrg(plan: string, body: OrgInvitationForm): Promise<void> {
    await this.orgsApi.inviteOrg(body, plan);
  }

  async setAgentPresence(body: AgentPresence): Promise<void> {
    await this.usersApi.setAgentPresence(body);
  }

  getConversation(room: ID): Promise<InboxEntry> {
    return this.roomsApi.getConversation(room);
  }

  getConversations({
    offset,
    limit,
    tab,
    tags,
    onlySnoozed,
    tagGroupIds,
    assigneeId,
    anyThreadClosedAfter,
    anyThreadClosedBefore,
    sorting
  }: GetConversationsParams): Promise<InboxState> {
    return this.conversationsApi.getConversations(
      limit,
      tab,
      offset,
      tags,
      onlySnoozed,
      assigneeId,
      anyThreadClosedAfter,
      anyThreadClosedBefore,
      sorting,
      tagGroupIds
    );
  }

  async setConversationStatus(room: ID, status: ConversationStatus): Promise<void> {
    await this.roomsApi.setConversationStatus({ status }, room);
  }

  getCompanyBillingData(): Promise<OrgBillingData> {
    return this.orgsApi.getCompanyBillingData();
  }

  async getActiveAgentProfiles(): Promise<AgentProfile[]> {
    const agents = await this.usersApi.getAgentsProfiles();
    const activeAgents = agents.filter((agent) => agent.isActive && !agent.isBot);
    return activeAgents;
  }

  getAgentProfiles(): Promise<AgentProfile[]> {
    return this.usersApi.getAgentsProfiles();
  }

  getAgentPreferences(id: string): Promise<AgentPreferences> {
    // TODO: unused param
    return this.usersApi.getAgentPreferences();
  }

  getPlanDetails(planName: string): Promise<BillingPlan> {
    return this.billingsApi.getPlan(planName);
  }

  async deleteSlots(): Promise<void> {
    await this.calendarsApi.deleteSlots();
  }

  importEvents(body: CalendarForm): Promise<CalendarSlot[]> {
    return this.calendarsApi.importEvents(body);
  }

  getCalendars(): Promise<CalendarInfo[]> {
    return this.calendarsApi.getCalendars();
  }

  getSlots(): Promise<CalendarSlot[]> {
    return this.calendarsApi.getSlots();
  }

  authorizeCode(code: AuthCode): Promise<CalendarInfo[]> {
    return this.calendarsApi.authorizeCode(code);
  }

  async follow(room: ID): Promise<void> {
    await this.roomsApi.follow(room);
  }

  async unfollow(room: ID): Promise<void> {
    await this.roomsApi.unfollow(room);
  }

  async addFollower(roomId: string, request: Follower): Promise<void> {
    await this.roomsApi.addFollower(request, roomId);
  }

  async setAssignee(room: ID, user: ID): Promise<void> {
    await this.roomsApi.setAssignee({ user }, room);
  }

  scheduleMeeting(room: string, start: number, duration: number): Promise<Meeting> {
    return this.meetingsApi.scheduleMeeting({ room, start, duration });
  }

  scheduleMeetingExternal(
    start: number,
    duration: number,
    profile: GuestProfileForm
  ): Promise<Meeting> {
    return this.meetingsApi.scheduleMeetingExternal({ start, duration, profile });
  }

  async rescheduleMeeting(id: string, start: number, duration: number): Promise<void> {
    await this.meetingsApi.rescheduleMeeting({ id, start, duration });
  }

  async cancelMeeting(id: string): Promise<void> {
    await this.meetingsApi.cancelMeeting({ id });
  }

  getRoomMeetings(roomId: ID): Promise<Meeting[]> {
    return this.meetingsApi.getRoomMeetings(roomId);
  }

  getMeetings(from: number, to: number): Promise<Meeting[]> {
    return this.meetingsApi.getMeetings(from, to);
  }

  getGuestProfilesByQuery(query: string): Promise<GuestProfile[]> {
    return this.roomsApi.getGuestProfilesByQuery(query);
  }

  searchGuest(query: string): Promise<GuestProfile[]> {
    return this.usersApi.searchGuest(query);
  }

  getGuestProfile(userId: string): Promise<GuestProfile> {
    return this.usersApi.getGuestProfile(this.orgId || "", userId);
  }

  getGuestProfileByRoom(roomId: string): Promise<GuestProfile> {
    return this.roomsApi.getGuestProfileByRoom(roomId);
  }

  async patchGuestProfile(userId: string, profile: GuestProfileFormPatch): Promise<void> {
    // prevent JSON.stringify from removing undefineds from data, which the backend would interpret as excluded from patch
    profile = Object.keys(profile).reduce(
      (obj, key: string) =>
        Object.assign(obj, {
          /* tslint:disable-next-line:no-null-keyword */
          [key]: (profile as any)[key] === undefined ? null : (profile as any)[key]
        }),
      {} as GuestProfileFormPatch
    );
    await this.usersApi.patchGuestProfile(profile, this.orgId || "", userId);
  }

  async patchAgentPreferences(form: PreferencesForm): Promise<void> {
    await this.usersApi.patchAgentPreferences(form);
  }

  async deleteGuestProfile(userId: string): Promise<void> {
    await this.usersApi.deleteCustomer(userId);
  }

  async uploadFile(file: File, roomId: string): Promise<IAttachment> {
    const handle = await this.filesApi.getFileHandleForAttachmentUpload(roomId);

    const form = new FormData();
    form.append("file", file);
    const fileUrl = getWheelhouseDownloadUrl(handle.fileToken);
    const response = await fetch(fileUrl, { method: "POST", body: form });

    return mapUploadResponse(response, file.type, handle.fileToken, handle.fileId);
  }

  async deleteFile(fileUpload: IFileUploadInfo): Promise<void> {
    if (fileUpload && fileUpload.attachment && this.apiKey) {
      await fetch(getWheelhouseDownloadUrl(fileUpload.attachment.token), {
        method: "DELETE",
        headers: { "X-Api-Key": this.apiKey }
      });
    }
  }

  async getTemporaryToken(fileToken: string): Promise<string> {
    return (await this.filesApi.getTemporaryToken(fileToken)).temporaryToken;
  }

  getAgentProfile(agentId: string): Promise<AgentProfile> {
    return this.usersApi.getAgentProfile(agentId);
  }

  async changeAgentAvatar(file: File): Promise<void> {
    try {
      const fileHandle = await this.usersApi.createAgentAvatarHandle();
      await this.doFileFetch(fileHandle.fileToken, file, "avatar.jpeg");
    } catch (error) {
      logger.error({ message: "Couldn't change agent avatar", error });
    }
  }

  putConfig(orgId: string, body: OrgConfig): Promise<OrgConfig> {
    return this.orgsApi.putConfig(body, orgId);
  }

  getOwnAgentContext(): Promise<AgentCtx> {
    return this.usersApi.getOwnAgentContext();
  }

  async changeOrgLogo(file: File, orgId: string): Promise<string> {
    const fileHandle = await this.orgsApi.createOrgLogoHandle(orgId);
    const response = await this.doFileFetch(fileHandle.fileToken, file);
    return response.url;
  }

  async changeOrgAgentAvatar(file: File, orgId: string): Promise<string> {
    const fileHandle = await this.orgsApi.createOrgDefaultAgentAvatarHandle(orgId);
    const response = await this.doFileFetch(fileHandle.fileToken, file);
    return response.url;
  }

  async changeOrgCover(file: File, orgId: string): Promise<string> {
    const fileHandle = await this.orgsApi.createOrgCoverHandle(orgId);
    const response = await this.doFileFetch(fileHandle.fileToken, file);
    return response.url;
  }

  getRemovedAgents(): Promise<AgentProfile[]> {
    return this.usersApi.getDeactivatedAdvisers();
  }

  getPendingAgents(): Promise<Invitee[]> {
    return this.inviteesApi.getInviteesByOrg();
  }

  async removeAgent(agentId: string): Promise<void> {
    await this.usersApi.deactivateAdviser(agentId);
  }

  async revokeInvitee(email: string): Promise<void> {
    await this.inviteesApi.revokeInvitation({ email });
  }

  async restoreAgent(agentId: string): Promise<void> {
    await this.usersApi.restoreAdviser(agentId);
  }

  async inviteManyAgents(invitees: InviteManyAdvisersForm): Promise<void> {
    await this.inviteesApi.inviteManyAdvisers(invitees);
  }

  getOrgCustomReasons(orgId: string): Promise<OrgReasons> {
    return this.orgsApi.getOrgReasons(orgId);
  }

  goToCustomerPortal(): Promise<PortalUrlResponse> {
    return this.billingsApi.getCustomerPortalSession();
  }

  getProactiveMessages(): Promise<ProactiveMsgDto[]> {
    return this.proactiveMsgApi.getProactiveMessages();
  }

  createProactiveMessage(form: CreateProactiveMsgForm): Promise<ProactiveMsgDto> {
    return this.proactiveMsgApi.createProactiveMessage(form);
  }

  async deleteProactiveMessage(id: string): Promise<void> {
    await this.proactiveMsgApi.deleteProactiveMessage(id);
  }

  updateProactiveMessage(id: string, body: UpdateProactiveMsgForm): Promise<ProactiveMsgDto> {
    return this.proactiveMsgApi.updateProactiveMessage(body, id);
  }

  getTagMappings(): Promise<TagMappingDto[]> {
    return this.tagMappingsApi.getTagMappings();
  }

  updateTagMapping(id: string, form: UpdateTagMappingForm): Promise<TagMappingDto> {
    return this.tagMappingsApi.updateTagMapping(form, id);
  }

  async deleteTagMapping(id: string): Promise<void> {
    await this.tagMappingsApi.deleteTagMapping(id);
  }

  createTagMapping(form: CreateTagMappingForm): Promise<TagMappingDto> {
    return this.tagMappingsApi.createTagMapping(form);
  }

  async deleteOrgDefaultAgentAvatar(orgId: string): Promise<void> {
    await this.orgsApi.deleteOrgDefaultAgentAvatar(orgId);
  }

  async deleteOrgCover(orgId: string): Promise<void> {
    await this.orgsApi.deleteOrgCover(orgId);
  }

  async deleteOrgLogo(orgId: string): Promise<void> {
    await this.orgsApi.deleteOrgLogo(orgId);
  }

  getAverageResponseTime(fromTimestamp: number, toTimestamp: number): Promise<AdviserAverageSpeedOfAnswerDto> {
    return this.usersApi.getAverageResponseTime(fromTimestamp, toTimestamp);
  }

  getCobrowseJwt(): Promise<AdviserCobrowseJwtResponse> {
    return this.usersApi.getCobrowseJwt();
  }

  async indicateCobrowsingStart(timestamp: number, roomId: string): Promise<void> {
    await this.cobrowsingApi.cobrowsingIndicateStart({ timestamp, roomId });
  }

  async indicateCobrowsingEnd(duration: number, roomId: string): Promise<void> {
    await this.cobrowsingApi.cobrowsingIndicateEnd({ duration, roomId });
  }

  async snooze(roomId: string): Promise<void> {
    await this.roomsApi.snooze(roomId);
  }

  async unsnooze(roomId: string): Promise<void> {
    await this.roomsApi.unsnooze(roomId);
  }

  getToast(): Promise<AdviserToast> {
    return this.billingsApi.getToast();
  }

  getIntents(locale: string): Promise<IntentWithDatasetInfo[]> {
    return this.nluApi.getIntents(locale);
  }

  createIntent(form: IntentForm): Promise<IntentDto> {
    return this.nluApi.createIntent(form);
  }

  updateIntent(id: string, form: IntentForm): Promise<IntentDto> {
    return this.nluApi.updateIntent(form, id);
  }

  async deleteIntent(id: string): Promise<void> {
    await this.nluApi.deleteIntent(id);
  }

  getDatasetEntries(intentId: string): Promise<DatasetEntryDto[]> {
    return this.nluApi.getDatasetEntries(intentId);
  }

  createDatasetEntry(intentId: string, form: DatasetEntryForm): Promise<DatasetEntryDto> {
    return this.nluApi.createDatasetEntry(form, intentId);
  }

  async deleteDatasetEntry(intentId: string, entryId: string): Promise<void> {
    await this.nluApi.deleteDatasetEntry(intentId, entryId);
  }

  async getLastTrainingInfo(locale: string): Promise<TrainingInfo | undefined> {
    const onlyLast = true;
    const [trainingInfo] = await this.nluApi.getTrainingInfo(onlyLast, locale);

    return trainingInfo;
  }

  trainModel(form: TrainModelForm): Promise<NewModel> {
    return this.nluApi.trainModel(form);
  }

  testSuggestion(form: TestSuggestionForm): Promise<SuggestionTestResult> {
    return this.nluApi.testSuggestion(form);
  }

  areAISuggestionsEnabled(): Promise<EnabledFlag> {
    return this.nluApi.areAISuggestionsEnabled();
  }

  setAISuggestionsEnabled(value: boolean): Promise<EnabledFlag> {
    return this.nluApi.setAISuggestionsEnabled({ enabled: value });
  }

  async sendMeetingLure(roomId: string): Promise<void> {
    await this.meetingsApi.sendMeetingLure(roomId);
  }

  async recallMeetingLure(roomId: string): Promise<void> {
    await this.meetingsApi.recallMeetingLure(roomId);
  }

  getEventActionsConfigs(): Promise<EventActionConfig[]> {
    return this.eventActionsApi.getEventActionConfigs();
  }

  createEventActionConfig(form: CreateEventActionConfigForm): Promise<EventActionConfig> {
    return this.eventActionsApi.createEventActionConfig(form);
  }

  updateEventActionConfig(
    id: string,
    form: UpdateEventActionConfigForm
  ): Promise<EventActionConfig> {
    return this.eventActionsApi.updateEventActionConfig(form, id);
  }

  async deleteEventActionConfig(id: string): Promise<void> {
    await this.eventActionsApi.deleteEventActionConfig(id);
  }

  async autoAssignConversation(roomId: string, body: AutoAssign): Promise<void> {
    await this.roomsApi.autoAssignConversation(body, roomId);
  }

  getProfanities(): Promise<ProfanitiesDto> {
    return this.profanitiesApi.getProfanities(this.orgId || "");
  }

  getWidgetForm(formId: string): Promise<WidgetFormSubmissionWithWidget> {
    return this.widgetsFormsSubmissionsApi.getWidgetFormByFormId(formId);
  }

  getMessageWidgets(locale: string): Promise<MessageWidgetConfig[]> {
    return this.messageWidgetsApi.getMessageWidgetsConfigs(locale);
  }

  updateMessageWidgetConfig(id: string, form: MessageWidgetForm): Promise<MessageWidgetConfig> {
    return this.messageWidgetsApi.updateMessageWidgetConfig(form, id);
  }

  createProfanities(form: ProfanityForm): Promise<ProfanitiesDto> {
    return this.profanitiesApi.createProfanities(form);
  }

  updateProfanities(form: ProfanityForm): Promise<ProfanitiesDto> {
    return this.profanitiesApi.updateProfanities(form);
  }

  getUnavailabilityReasons(): Promise<UnavailabilityReason[]> {
    return this.agentStatusesApi.getUnavailabilityReasons();
  }

  createUnavailabilityReason(body: UnavailabilityReasonCreateForm): Promise<UnavailabilityReason> {
    return this.agentStatusesApi.createUnavailabilityReason(body);
  }

  updateUnavailabilityReasons(
    reasonSlug: string,
    body: UnavailabilityReasonUpdateForm
  ): Promise<UnavailabilityReason> {
    return this.agentStatusesApi.updateUnavailabilityReasons(body, reasonSlug);
  }

  async deleteUnavailabilityReason(reasonSlug: string): Promise<void> {
    await this.agentStatusesApi.deleteUnavailabilityReason(reasonSlug);
  }

  getTags(): Promise<TagDto[]> {
    return this.tagsApi.getTags();
  }

  createTag(form: CreateTagForm): Promise<TagDto> {
    return this.tagsApi.createTag(form);
  }

  updateTag(id: string, form: UpdateTagForm): Promise<TagDto> {
    return this.tagsApi.updateTag(form, id);
  }

  async deleteTag(id: string): Promise<void> {
    await this.tagsApi.deleteTag(id);
  }

  getOrgRoles(): Promise<Role[]> {
    return this.rolesApi.getRolesForOrg();
  }

  createRole(body: CreateRoleForm): Promise<Role> {
    return this.rolesApi.createRole(body);
  }

  updateRole(roleId: string, body: UpdateRoleForm): Promise<Role> {
    return this.rolesApi.updateRole(body, roleId);
  }

  async deleteRole(roleId: string): Promise<void> {
    await this.rolesApi.deleteRole(roleId);
  }

  getTagGroups(): Promise<TagGroupDto[]> {
    return this.tagGroupsApi.getTagGroups();
  }

  createTagGroup(form: CreateTagGroupForm): Promise<TagGroupDto> {
    return this.tagGroupsApi.createTagGroup(form);
  }

  updateTagGroup(id: string, form: UpdateTagGroupForm): Promise<TagGroupDto> {
    return this.tagGroupsApi.updateTagGroup(form, id);
  }

  async deleteTagGroup(id: string): Promise<void> {
    await this.tagGroupsApi.deleteTagGroup(id);
  }

  updateTagGroups(groups: TagGroupDto[]): Promise<TagGroupDto[]> {
    return this.tagGroupsApi.updateTagGroups(groups);
  }

  async patchAdviser(id: string, form: AdviserPatchForm): Promise<void> {
    await this.usersApi.patchAdviser(form, id);
  }
  async patchAdvisers(forms: AdviserBatchPatchForm[]): Promise<void> {
    await this.usersApi.patchAdvisers(forms);
  }

  async resendInvitation(form: ResendInvitationForm): Promise<void> {
    await this.inviteesApi.resendInvitation(form);
  }

  getAgentGroups(): Promise<AgentGroup[]> {
    return this.agentGroupsApi.getAgentGroups();
  }

  async patchAgentGroup(form: AgentGroupPatchDto, agentGroupId: string): Promise<AgentGroup> {
    return this.agentGroupsApi.patchAgentGroup(form, agentGroupId);
  }

  async patchAgentGroups(forms: AgentGroupBatchPatchDto[]): Promise<AgentGroup[]> {
    return this.agentGroupsApi.patchAgentGroups(forms);
  }

  async deleteAgentGroup(id: string): Promise<void> {
    await this.agentGroupsApi.deleteAgentGroup(id);
  }

  createAgentGroup(form: CreateAgentGroupForm): Promise<AgentGroup> {
    return this.agentGroupsApi.createAgentGroup(form);
  }

  sendMessageWidgetForm(body: AddWidgetForm): Promise<WidgetFormSubmission> {
    return this.widgetsFormsSubmissionsApi.createWidgetFormForRoomId(body);
  }

  getOIDCConfig(orgId: string): Promise<OpenIDConnectConfig> {
    return this.oidcApi.getOpenIDConnectConfigUnauthorized(orgId);
  }

  getNonce(): Promise<Nonce> {
    return this.oidcApi.getNonce();
  }

  authorizeOpenIdConnect(form: CodeAuthorizationForm): Promise<AgentCtx> {
    return this.oidcApi.authorizeOpenIdConnect(form, { credentials: "include" });
  }

  getOrgBotProfile(): Promise<AgentProfile> {
    return this.usersApi.getBotAgentProfile();
  }

  async updateBotPersonalData(botData: BotPersonalDataForm): Promise<void> {
    await this.botsApi.updateBotPersonalData(botData);
  }

  getWebhooks(): Promise<WebhooksDTO> {
    return this.webhooksAPi.getWebhooks();
  }

  async updateWebhooks(form: WebhooksDTO): Promise<void> {
    await this.webhooksAPi.updateWebhooks(form);
  }

  getTopics(): Promise<TopicDTO[]> {
    return this.topicsApi.getTopics();
  }

  checkVat(vatRequestForm: VatRequestForm): Promise<VatResponse> {
    return this.billingsApi.checkVat(vatRequestForm);
  }

  getMainNotifications(): Promise<MainNotificationDto[]> {
    return this.mainNotificationsApi.getMainNotifications();
  }
  createMainNotification(form: MainNotificationPostForm): Promise<MainNotificationDto> {
    return this.mainNotificationsApi.createMainNotification(form);
  }
  patchMainNotification(id: string, form: MainNotificationPatchForm): Promise<MainNotificationDto> {
    return this.mainNotificationsApi.patchMainNotification(form, id);
  }

  getTagGroupRules(): Promise<TransferRuleDto[]> {
    return this.tagGroupTransfersApi.getTagGroupTransfers();
  }
  getTagGroupRule(tagGroupId: string): Promise<TransferRuleDto> {
    return this.tagGroupTransfersApi.getTagGroupTransfer(tagGroupId);
  }
  createTagGroupRules(form: TransferRuleDto): Promise<TransferRuleDto> {
    return this.tagGroupTransfersApi.createTagGroupTransfers(form);
  }
  patchTagGroupRule(
    tagGroupId: string,
    form: TransferRulePatchForm
  ): Promise<TransferRulePatchForm> {
    return this.tagGroupTransfersApi.patchTagGroupTransfers(form, tagGroupId);
  }
  async deleteTagGroupRule(tagGroupId: string): Promise<void> {
    await this.tagGroupTransfersApi.deleteTagGroupTransfers(tagGroupId);
  }
  getTagGroupTransfersParameters(): Promise<TransferParametersDto> {
    return this.tagGroupTransfersParametersApi.getTagGroupTransfersParameters();
  }
  patchTagGroupTransfersParameters(form: TransferParametersDto): Promise<TransferParametersDto> {
    return this.tagGroupTransfersParametersApi.patchTagGroupTransfersParameters(form);
  }
}
